import { useQuery } from '@tanstack/react-query'
import { type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import {
  fetchHospitals,
  hospitalsQueryKey,
  type HospitalListItem,
} from '../api/hospitals'
import type { ChipSelection } from '../components/chips'
import { Icon } from '../components/Icon'
import { MiniHospitalCard } from '../components/MiniHospitalCard'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { BED_TYPE_DEFS, type BedTypeDef } from '../lib/bedTypes'
import { useGeolocation, type Geolocation } from '../lib/useGeolocation'

const NO_FILTER: ChipSelection = { row1: [], row2: null }

/** Network-wide free count + number of hospitals with any free, for a type. */
function aggregate(
  hospitals: HospitalListItem[],
  def: BedTypeDef,
): { free: number; hospitals: number } {
  let free = 0
  let count = 0
  for (const h of hospitals) {
    const n = h.beds[def.free]
    free += n
    if (n > 0) count += 1
  }
  return { free, hospitals: count }
}

/**
 * How the results are ordered (M21-T05).
 *
 * `free` is the DEFAULT because it needs no permission and it is what a patient
 * hunting a bed actually wants. `near` needs location, and the page never
 * pretends otherwise — see `effectiveSort`.
 */
type SortKey = 'free' | 'near'

/** The hospitals that have this type free, in the requested order. */
function withFree(
  hospitals: HospitalListItem[],
  def: BedTypeDef,
  sort: SortKey,
): HospitalListItem[] {
  const list = hospitals.filter((h) => h.beds[def.free] > 0)
  // Sorted here rather than by re-querying: changing the order must not throw
  // the list away and fetch it again. Only GRANTING location refetches, because
  // only the server can attach `distance_km`.
  return [...list].sort((a, b) =>
    sort === 'near'
      ? (a.distance_km ?? Infinity) - (b.distance_km ?? Infinity)
      : b.beds[def.free] - a.beds[def.free],
  )
}

function miniArea(h: HospitalListItem): string {
  return h.distance_km != null ? `${h.city} · ${h.distance_km} km` : h.city
}

/**
 * `/beds` — choose a bed type, and see which hospitals have it (M21-T02,
 * ADR-0046).
 *
 * IT USED TO BE AN INTERSTITIAL. Five types, and tapping one called
 * `bedTypeDestination()` and navigated to `/?row1=…&bedtype=…`, leaving this
 * page with nothing on it — its whole purpose was to hand off. The owner
 * compared it to the Figma frame (534:1000), which puts the hospitals on this
 * page, and ruled the extra screen a click the patient should not spend.
 *
 * So the type is now a FILTER rather than a destination. Selecting one reveals
 * the hospitals that have it, in place; selecting another re-filters without
 * navigation.
 *
 * NO SECOND FETCH, and that is what keeps the numbers honest. The page already
 * loads every hospital for the network-wide totals beside each type, so the
 * per-hospital breakdown is a filter over the SAME array. The heading count, the
 * card count and the totals above cannot disagree, because there is only one
 * source for all three — the failure this page could most easily have shipped is
 * "4 hospitals" written above three cards.
 *
 * Still no prices: a price only ever shows attached to a chosen type, on
 * `/book/:slug?type=…`. ADR-0046 reaffirms that and explains why the frame's
 * "Ghc 700/night" line is not built — there is no per-bed-type price in the
 * system at all.
 */
export function BedTypePicker(): ReactNode {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const geo = useGeolocation()
  const query = useQuery({
    // Coords are part of the key, so GRANTING location refetches once and the
    // server attaches `distance_km`. Toggling the sort afterwards does not —
    // that is a client-side reorder of data already in hand.
    queryKey: hospitalsQueryKey(NO_FILTER, geo.coords),
    queryFn: ({ signal }) => fetchHospitals(NO_FILTER, geo.coords, signal),
  })

  // `?type=` matches `/book/:slug?type=…`, so the whole bed journey names the
  // type the same way. An unknown value falls back to no selection rather than
  // an error — a shared link with a stale type should still open the page.
  const selected =
    BED_TYPE_DEFS.find((d) => d.key === params.get('type')) ?? null

  // In the URL rather than in `useState`, so Back leaves a selection and the
  // page is shareable. `replace` keeps one history entry per visit instead of
  // one per tap — a patient comparing three types should not have to press Back
  // three times to leave.
  const select = (def: BedTypeDef): void => {
    const next = new URLSearchParams(params)
    if (def.key === selected?.key) next.delete('type')
    else next.set('type', def.key)
    setParams(next, { replace: true })
  }

  // What the patient ASKED for, which is not always what they can have.
  const requestedSort: SortKey = params.get('sort') === 'near' ? 'near' : 'free'

  /**
   * What the list is ACTUALLY ordered by.
   *
   * The defect this ticket exists to avoid is a "Closest" control that sits
   * there selected while the list is in some other order because location was
   * refused — a control that lies about what you are looking at. So `near` only
   * takes effect once coords exist, the control displays THIS value rather than
   * the requested one, and a note says why when the two differ.
   */
  const sort: SortKey = requestedSort === 'near' && geo.coords ? 'near' : 'free'

  const changeSort = (next: SortKey): void => {
    const p = new URLSearchParams(params)
    if (next === 'free') p.delete('sort')
    else p.set('sort', 'near')
    setParams(p, { replace: true })
    // Asking for "closest" is what prompts for location; the page never asks
    // unbidden. Re-asking after a refusal is pointless, so it does not.
    if (next === 'near' && !geo.coords && geo.status === 'idle') geo.request()
  }

  return (
    <PageShell measure={594} as="main" className="font-sans text-ink-1">
      <header
        className={`flex items-center gap-2 border-b border-line-1 px-4 py-3 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back to home"
          onClick={() => navigate('/')}
          className="rounded-full p-1 text-ink-3 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={20} />
        </button>
        <div>
          <h1 className="text-base font-bold tracking-tight">Find a bed</h1>
          <p className="text-[11px] text-ink-3">
            Live counts across the 10 partner hospitals
          </p>
        </div>
      </header>
      {/* The SELECTOR sits on the 594 measure; the results below do not. That
          is the frame, not a compromise: 534:1000 draws the search-and-chips
          block 471px wide and centred, and the hospital list 1384px wide across
          the same 1440 page.

          Rendering is what settled it. Left inside this band, the four-up grid
          divided 594 and the cards came out 128.5px at 1440 — a photograph card
          narrower than a phone's. JSDOM cannot see that; the suite was green. */}
      <PageBand className="flex flex-col" contentClassName="flex flex-col">
        <div className="flex flex-col gap-4 px-4 pt-5">
          <p className="font-mono text-[10px] uppercase tracking-wide text-ink-3">
            Which bed type do you need?
          </p>

          {query.isPending ? (
            <PickerSkeleton />
          ) : query.isError ? (
            <div
              role="alert"
              className="flex flex-col items-center gap-1 py-12 text-center"
            >
              <p className="text-[13px] text-ink-3">
                Couldn&apos;t load bed availability.
              </p>
              <button
                type="button"
                onClick={() => void query.refetch()}
                className="mt-2 rounded-full bg-accent px-4 py-1.5 text-[13px] font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                Retry
              </button>
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {BED_TYPE_DEFS.map((def) => {
                const agg = aggregate(query.data, def)
                const none = agg.free === 0
                const isSelected = def.key === selected?.key
                return (
                  <li key={def.key}>
                    {/* `aria-pressed`, not `role="radio"`. These are mutually
                        exclusive, so a radiogroup is the tempting read — but a
                        radiogroup owes the user arrow-key navigation and a
                        roving tabindex, and a half-built one behaves worse than
                        a plain toggle. Colour is never the only signal
                        (CLAUDE.md), so the state is announced as well as drawn. */}
                    <button
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => select(def)}
                      className={`flex w-full items-center gap-3 rounded-r3 border bg-bg-app px-3.5 py-3 text-left shadow-xs transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
                        isSelected
                          ? 'border-accent ring-1 ring-inset ring-accent'
                          : 'border-line-1'
                      }`}
                    >
                      <Icon name="bed" size={18} className="text-ink-2" />
                      <span className="flex-1 text-sm font-semibold text-ink-1">
                        {def.label}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                          none
                            ? 'bg-bed-bad-bg text-bed-bad ring-bed-bad/15'
                            : 'bg-bed-good-bg text-bed-good ring-bed-good/15'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${none ? 'bg-bed-bad' : 'bg-bed-good'}`}
                          aria-hidden="true"
                        />
                        <span className="tabular-nums">
                          {none
                            ? 'None free'
                            : `${agg.free} free · ${agg.hospitals} hosp.`}
                        </span>
                      </span>
                      {/* The chevron went with the navigation. This row no
                          longer takes you anywhere, and an affordance that
                          promises a journey it does not make is worse than no
                          affordance. A tick marks what a tap actually does. */}
                      <Icon
                        name={isSelected ? 'check' : 'chevd'}
                        size={14}
                        className={isSelected ? 'text-accent' : 'text-ink-3'}
                      />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

        </div>
      </PageBand>

      {/* The results region. Full-bleed on purpose (see the band above), and
          ANNOUNCED, because it changes in place: a patient tapping a different
          type gets a new list with no navigation, and a silent swap is
          invisible to a screen reader. */}
      <section
        aria-live="polite"
        aria-label="Hospitals with the selected bed type"
        className={`flex flex-1 flex-col px-4 py-4 ${PAGE_GUTTER_DESKTOP}`}
      >
        {selected && query.data && (
          <BedTypeResults
            def={selected}
            hospitals={withFree(query.data, selected, sort)}
            sort={sort}
            requestedSort={requestedSort}
            geo={geo}
            onSortChange={changeSort}
            onSelectHospital={(slug) => navigate(`/hospital/${slug}`)}
            onSeeAll={() =>
              navigate(
                selected.chip
                  ? `/?row1=${selected.chip}&bedtype=${selected.key}`
                  : `/?bedtype=${selected.key}`,
              )
            }
          />
        )}
      </section>

      <PageBand className="flex flex-col" contentClassName="flex flex-col">
        <p className="px-4 pb-5 text-[12px] text-ink-3">
          Prices appear once you choose a hospital and bed type.
        </p>
      </PageBand>
    </PageShell>
  )
}

function PickerSkeleton(): ReactNode {
  return (
    <ul aria-hidden="true" className="flex flex-col gap-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="h-[52px] animate-pulse rounded-r3 border border-line-1 bg-bg-sunken"
        />
      ))}
    </ul>
  )
}

/**
 * The hospitals that have the selected type free.
 *
 * Derived, never fetched — see the note on `BedTypePicker`. `hospitals` is
 * already filtered by the caller from the same array the totals above are
 * computed from, so the heading count and the card count are the same number by
 * construction rather than by agreement.
 */
function BedTypeResults({
  def,
  hospitals,
  sort,
  requestedSort,
  geo,
  onSortChange,
  onSelectHospital,
  onSeeAll,
}: {
  def: BedTypeDef
  hospitals: HospitalListItem[]
  sort: SortKey
  requestedSort: SortKey
  geo: Geolocation
  onSortChange: (next: SortKey) => void
  onSelectHospital: (slug: string) => void
  onSeeAll: () => void
}): ReactNode {
  if (hospitals.length === 0) {
    // NOT a rare path. `Executive suite` regularly sits at zero across the whole
    // network, so this is what a real patient sees on an ordinary day — which is
    // why it gets a way onward rather than a full stop (M21-T03).
    //
    // The way onward is the results screen for this same type. It lists every
    // partner ordered by that type's free count, so a patient can see who
    // usually has one and call ahead, instead of being told "no" and left there.
    // It is the `?bedtype=` deep link T04 kept alive, used deliberately.
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <p className="text-[13px] text-ink-2">
          No partner hospital has {def.noun}s free right now.
        </p>
        <p className="text-[12px] text-ink-3">
          Counts change through the day. Try another bed type above, or see how
          the hospitals compare.
        </p>
        <button
          type="button"
          onClick={onSeeAll}
          className="mt-1 rounded-r3 border border-line-1 px-3 py-1.5 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          See all hospitals
        </button>
      </div>
    )
  }

  return (
    <>
      {/* Heading and sort control on one row, as the frame draws them
          (538:711 — the section title left, the sort option right). */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
        <h2 className="text-sm font-semibold text-ink-1">
          {hospitals.length === 1
            ? `1 hospital has ${def.noun}s free`
            : `${hospitals.length} hospitals have ${def.noun}s free`}
        </h2>
        <SortControl
          sort={sort}
          requestedSort={requestedSort}
          geo={geo}
          onChange={onSortChange}
        />
      </div>
      {/* The frame lays these out horizontally, and the home strip already
          solves it: scroll on a phone, a grid from `lg`. Same classes on
          purpose — two strips of the same card that scrolled differently would
          be a bug nobody filed. */}
      <div className="no-scrollbar flex gap-2.5 overflow-x-auto pb-1 lg:grid lg:grid-cols-4 lg:gap-4 lg:overflow-visible">
        {hospitals.map((h) => (
          <MiniHospitalCard
            key={h.id}
            name={h.name}
            area={miniArea(h)}
            slug={h.slug}
            emergencyStatus={h.beds.emergency_status}
            bedType={def}
            bedFree={h.beds[def.free]}
            onSelect={() => onSelectHospital(h.slug)}
          />
        ))}
      </div>
    </>
  )
}

/**
 * Names the order the list is actually in (M21-T05).
 *
 * `value` is the EFFECTIVE sort, never the requested one. If a patient asks for
 * "Closest" and refuses location, this drops back to "Most beds free" and says
 * why — rather than sitting on "Closest" beside a list that is not sorted by
 * distance. A control that misnames what you are looking at is worse than no
 * control, and it is the specific failure this ticket was written against.
 *
 * "Closest" is never disabled outright: on a first visit nobody has been asked
 * yet, and the request only happens because the patient chose it. Location
 * never gates the list (M3) and does not gate it here either — the unsorted
 * order remains fully usable.
 */
function SortControl({
  sort,
  requestedSort,
  geo,
  onChange,
}: {
  sort: SortKey
  requestedSort: SortKey
  geo: Geolocation
  onChange: (next: SortKey) => void
}): ReactNode {
  const wanted = requestedSort === 'near' && sort !== 'near'
  const note = !wanted
    ? null
    : geo.status === 'prompting'
      ? 'Finding your location…'
      : geo.status === 'denied'
        ? 'Location off — sorted by most beds free instead.'
        : geo.status === 'unavailable'
          ? "Location isn't available on this device — sorted by most beds free."
          : null

  return (
    <div className="flex flex-col items-end gap-0.5">
      <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-2">
        <span>Sort</span>
        <select
          value={sort}
          onChange={(e) => onChange(e.target.value as SortKey)}
          className="rounded-r3 border border-line-1 bg-bg-app px-2 py-1 text-[12px] text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <option value="free">Most beds free</option>
          <option value="near">Closest</option>
        </select>
      </label>
      {note !== null && (
        <p role="status" className="text-[11px] text-ink-3">
          {note}
        </p>
      )}
    </div>
  )
}
